import { StrapiFilter, StrapiFilterOperator } from '../types/strapi.types';
import { TeamFilter, TeamFilterOperator } from '../types/team.types';
import { AdvancedFilterConfig } from '../../types/filter';

/**
 * Mapeo de operadores internos de la tabla a operadores de Strapi
 * @see https://docs.strapi.io/cms/api/rest/filters
 */
const OPERATOR_MAP: Record<TeamFilterOperator, StrapiFilterOperator> = {
  equals: '$eq',
  notEquals: '$ne',
  contains: '$containsi', // Case insensitive por defecto
  notContains: '$notContainsi',
  startsWith: '$startsWithi',
  endsWith: '$endsWithi',
  gt: '$gt',
  gte: '$gte',
  lt: '$lt',
  lte: '$lte',
  between: '$between',
  in: '$in',
  isEmpty: '$null',
  isNotEmpty: '$notNull',
  afterDate: '$gt',
  beforeDate: '$lt',
  thisMonth: '$gte', // Se manejará con valor calculado
  lastMonth: '$gte', // Se manejará con valor calculado
  thisYear: '$gte', // Se manejará con valor calculado
  lastYear: '$gte', // Se manejará con valor calculado
};

/**
 * Obtiene el primer y último día del mes actual
 */
function getThisMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

/**
 * Obtiene el primer y último día del mes anterior
 */
function getLastMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

/**
 * Obtiene el primer y último día del año actual
 */
function getThisYearRange(): { start: string; end: string } {
  const now = new Date();
  return {
    start: `${now.getFullYear()}-01-01`,
    end: `${now.getFullYear()}-12-31`,
  };
}

/**
 * Obtiene el primer y último día del año anterior
 */
function getLastYearRange(): { start: string; end: string } {
  const now = new Date();
  const lastYear = now.getFullYear() - 1;
  return {
    start: `${lastYear}-01-01`,
    end: `${lastYear}-12-31`,
  };
}

/**
 * Convierte un filtro de la tabla a formato de filtro de Strapi
 */
export function convertFilterToStrapi(filter: TeamFilter): StrapiFilter {
  const { field, operator, value, value2 } = filter;
  const strapiOperator = OPERATOR_MAP[operator];

  // Manejar operadores de fecha relativa
  if (operator === 'thisMonth') {
    const range = getThisMonthRange();
    return {
      [field]: {
        $gte: range.start,
        $lte: range.end,
      },
    };
  }

  if (operator === 'lastMonth') {
    const range = getLastMonthRange();
    return {
      [field]: {
        $gte: range.start,
        $lte: range.end,
      },
    };
  }

  if (operator === 'thisYear') {
    const range = getThisYearRange();
    return {
      [field]: {
        $gte: range.start,
        $lte: range.end,
      },
    };
  }

  if (operator === 'lastYear') {
    const range = getLastYearRange();
    return {
      [field]: {
        $gte: range.start,
        $lte: range.end,
      },
    };
  }

  // Manejar operador 'between'
  if (operator === 'between' && value2 !== undefined) {
    return {
      [field]: {
        $gte: value,
        $lte: value2,
      },
    };
  }

  // Manejar operadores null
  if (operator === 'isEmpty') {
    return {
      [field]: {
        $null: true,
      },
    };
  }

  if (operator === 'isNotEmpty') {
    return {
      [field]: {
        $notNull: true,
      },
    };
  }

  // Caso general
  return {
    [field]: {
      [strapiOperator]: value,
    },
  };
}

/**
 * Convierte un filtro del formato AdvancedFilterConfig a TeamFilter
 */
export function advancedFilterToTeamFilter(filter: AdvancedFilterConfig): TeamFilter {
  return {
    field: filter.columnId,
    operator: filter.operator as TeamFilterOperator,
    value: filter.value,
    value2: filter.value2,
    logicOperator: filter.logicOperator,
  };
}

/**
 * Convierte múltiples filtros de la tabla a formato de query string de Strapi
 * Maneja la lógica AND/OR entre filtros
 */
export function convertFiltersToStrapiQuery(filters: TeamFilter[]): StrapiFilter {
  if (filters.length === 0) {
    return {};
  }

  if (filters.length === 1) {
    return convertFilterToStrapi(filters[0]);
  }

  // Agrupar filtros por su operador lógico
  // Por defecto, Strapi combina filtros con AND implícito
  // Necesitamos manejar explícitamente OR

  const result: StrapiFilter = {};
  const orGroups: StrapiFilter[][] = [];
  let currentAndGroup: StrapiFilter[] = [];

  filters.forEach((filter, index) => {
    const strapiFilter = convertFilterToStrapi(filter);
    currentAndGroup.push(strapiFilter);

    // Si el operador lógico es OR, cerrar el grupo actual y empezar uno nuevo
    if (filter.logicOperator === 'or' && index < filters.length - 1) {
      orGroups.push([...currentAndGroup]);
      currentAndGroup = [];
    }
  });

  // Agregar el último grupo
  if (currentAndGroup.length > 0) {
    orGroups.push(currentAndGroup);
  }

  // Si solo hay un grupo AND, combinamos los filtros directamente
  if (orGroups.length === 1) {
    return Object.assign({}, ...orGroups[0]);
  }

  // Si hay múltiples grupos (OR), usamos $or
  return {
    $or: orGroups.map((group) => {
      if (group.length === 1) {
        return group[0];
      }
      return { $and: group };
    }),
  };
}

/**
 * Convierte filtros del formato AdvancedFilterConfig al formato de Strapi
 */
export function convertAdvancedFiltersToStrapi(filters: AdvancedFilterConfig[]): StrapiFilter {
  const teamFilters = filters.map(advancedFilterToTeamFilter);
  return convertFiltersToStrapiQuery(teamFilters);
}

/**
 * Genera la URL con query params para Strapi
 * Usa el formato de filtros de Strapi: filters[field][$operator]=value
 */
export function buildStrapiQueryString(params: {
  filters?: StrapiFilter;
  sort?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}): string {
  const searchParams = new URLSearchParams();

  // Agregar paginación
  if (params.page !== undefined) {
    searchParams.append('pagination[page]', params.page.toString());
  }
  if (params.pageSize !== undefined) {
    searchParams.append('pagination[pageSize]', params.pageSize.toString());
  }

  // Agregar ordenamiento
  if (params.sort) {
    const direction = params.sortDirection === 'desc' ? ':desc' : ':asc';
    searchParams.append('sort', `${params.sort}${direction}`);
  }

  // Agregar filtros de forma recursiva
  if (params.filters && Object.keys(params.filters).length > 0) {
    appendFiltersToParams(searchParams, params.filters, 'filters');
  }

  return searchParams.toString();
}

/**
 * Función auxiliar para agregar filtros al URLSearchParams de forma recursiva
 */
function appendFiltersToParams(
  searchParams: URLSearchParams,
  filters: StrapiFilter,
  prefix: string
): void {
  Object.entries(filters).forEach(([key, value]) => {
    const newPrefix = `${prefix}[${key}]`;

    if (value === null || value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      // Para arrays como $in
      value.forEach((item, index) => {
        searchParams.append(`${newPrefix}[${index}]`, String(item));
      });
    } else if (typeof value === 'object') {
      // Recursivo para objetos anidados
      appendFiltersToParams(searchParams, value as StrapiFilter, newPrefix);
    } else {
      // Valor primitivo
      searchParams.append(newPrefix, String(value));
    }
  });
}
